import React from 'react';
import { Button as MuiButton } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';

// Utility functions for dynamic theme handling
const createGradient = (color1, color2, direction = '135deg') => 
  `linear-gradient(${direction}, ${color1} 0%, ${color2} 100%)`;

const createShadow = (color, opacity = 0.3, size = 'medium') => {
  const sizes = {
    small: '0px 2px 8px',
    medium: '0px 4px 12px',
    large: '0px 8px 25px',
    xlarge: '0px 12px 35px',
  };
  return `${sizes[size]} ${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
};

// Enhanced Button component with consistent styling and multiple variants
const StyledButton = styled(MuiButton)(({ theme, buttonType, colorVariant }) => {
  const getButtonConfig = () => {
    switch (buttonType) {
      case 'auth':
        return {
          borderRadius: theme.spacing(2),
          padding: theme.spacing(1.5, 3),
          fontSize: '1.1rem',
          boxShadow: createShadow(theme.palette.primary.main, 0.4, 'large'),
          '&:hover': {
            boxShadow: createShadow(theme.palette.primary.main, 0.5, 'xlarge'),
            transform: 'translateY(-2px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
        };
      case 'action':
        return {
          borderRadius: 0,
          padding: theme.spacing(1, 3),
          fontSize: '0.95rem',
          '&:hover': {
            boxShadow: createShadow(theme.palette.primary.main, 0.3, 'medium'),
            transform: 'translateY(-1px)',
          },
        };
      case 'form':
        return {
          borderRadius: 0,
          padding: theme.spacing(1, 2.5),
          fontSize: '0.9rem',
          minWidth: 100,
          '&:hover': {
            boxShadow: createShadow(theme.palette.primary.main, 0.2, 'small'),
            transform: 'translateY(-1px)',
          },
        };
      case 'icon':
        return {
          borderRadius: 0,
          padding: theme.spacing(0.75, 1.5),
          fontSize: '0.875rem',
          minWidth: 'auto',
          '&:hover': {
            boxShadow: createShadow(theme.palette.primary.main, 0.2, 'small'),
            transform: 'scale(1.05)',
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
        };
      default:
        return {
          borderRadius: 0,
          padding: theme.spacing(1, 3),
          fontSize: '0.95rem',
          '&:hover': {
            boxShadow: createShadow(theme.palette.primary.main, 0.3, 'medium'),
            transform: 'translateY(-1px)',
          },
        };
    }
  };

  const getColorStyles = () => {
    // Dynamic color selection based on colorVariant
    const paletteColor = theme.palette[colorVariant] || theme.palette.primary;
    
    const baseGradient = createGradient(paletteColor.main, paletteColor.dark);
    const reverseGradient = createGradient(paletteColor.dark, paletteColor.main);
    const borderColor = paletteColor.main;
    const textColor = paletteColor.main;
    const hoverBgColor = paletteColor[50] || `${paletteColor.main}10`;

    return {
      '&.MuiButton-contained': {
        background: baseGradient,
        color: theme.palette.common.white,
        '&:hover': {
          background: reverseGradient,
          color: theme.palette.common.white,
        },
      },
      '&.MuiButton-outlined': {
        borderColor,
        color: textColor,
        '&:hover': {
          backgroundColor: hoverBgColor,
          borderColor,
        },
      },
      '&.MuiButton-text': {
        color: textColor,
        '&:hover': {
          backgroundColor: hoverBgColor,
        },
      },
    };
  };

  const buttonConfig = getButtonConfig();
  const colorStyles = getColorStyles();

  return {
    fontWeight: 600,
    textTransform: 'none',
    transition: 'all 0.2s ease-in-out',
    boxShadow: 'none',
    ...buttonConfig,
    ...colorStyles,
  };
});

// Enhanced LoadingButton with consistent styling
const StyledLoadingButton = styled(LoadingButton)(({ theme, buttonType, colorVariant }) => {
  const getButtonConfig = () => {
    switch (buttonType) {
      case 'auth':
        return {
          borderRadius: theme.spacing(2),
          padding: theme.spacing(1.5, 3),
          fontSize: '1.1rem',
          boxShadow: createShadow(theme.palette.primary.main, 0.4, 'large'),
          '&:hover': {
            boxShadow: createShadow(theme.palette.primary.main, 0.5, 'xlarge'),
            transform: 'translateY(-2px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
        };
      case 'action':
        return {
          borderRadius: 0,
          padding: theme.spacing(1, 3),
          fontSize: '0.95rem',
          '&:hover': {
            boxShadow: createShadow(theme.palette.primary.main, 0.3, 'medium'),
            transform: 'translateY(-1px)',
          },
        };
      case 'form':
        return {
          borderRadius: 0,
          padding: theme.spacing(1, 2.5),
          fontSize: '0.9rem',
          minWidth: 100,
          '&:hover': {
            boxShadow: createShadow(theme.palette.primary.main, 0.2, 'small'),
            transform: 'translateY(-1px)',
          },
        };
      case 'icon':
        return {
          borderRadius: 0,
          padding: theme.spacing(0.75, 1.5),
          fontSize: '0.875rem',
          minWidth: 'auto',
          '&:hover': {
            boxShadow: createShadow(theme.palette.primary.main, 0.2, 'small'),
            transform: 'scale(1.05)',
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
        };
      default:
        return {
          borderRadius: 0,
          padding: theme.spacing(1, 3),
          fontSize: '0.95rem',
          '&:hover': {
            boxShadow: createShadow(theme.palette.primary.main, 0.3, 'medium'),
            transform: 'translateY(-1px)',
          },
        };
    }
  };

  const getColorStyles = () => {
    // Dynamic color selection based on colorVariant
    const paletteColor = theme.palette[colorVariant] || theme.palette.primary;
    
    const baseGradient = createGradient(paletteColor.main, paletteColor.dark);
    const reverseGradient = createGradient(paletteColor.dark, paletteColor.main);
    const borderColor = paletteColor.main;
    const textColor = paletteColor.main;
    const hoverBgColor = paletteColor[50] || `${paletteColor.main}10`;

    return {
      '&.MuiButton-contained': {
        background: baseGradient,
        color: theme.palette.common.white,
        '&:hover': {
          background: reverseGradient,
          color: theme.palette.common.white,
        },
      },
      '&.MuiButton-outlined': {
        borderColor,
        color: textColor,
        '&:hover': {
          backgroundColor: hoverBgColor,
          borderColor,
        },
      },
      '&.MuiButton-text': {
        color: textColor,
        '&:hover': {
          backgroundColor: hoverBgColor,
        },
      },
    };
  };

  const buttonConfig = getButtonConfig();
  const colorStyles = getColorStyles();

  return {
    fontWeight: 600,
    textTransform: 'none',
    transition: 'all 0.2s ease-in-out',
    boxShadow: 'none',
    ...buttonConfig,
    ...colorStyles,
  };
});

const EasyCheckButton = ({
  children,
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  buttonType = 'default',
  loading = false,
  disabled = false,
  fullWidth = false,
  startIcon,
  endIcon,
  onClick,
  type = 'button',
  className,
  sx,
  ...props
}) => {
  // Map semantic color names to palette colors
  const getColorVariant = () => {
    switch (color) {
      case 'danger':
        return 'error';
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      case 'primary':
      default:
        return 'primary';
    }
  };

  const colorVariant = getColorVariant();

  // Determine which component to use based on loading state
  const ButtonComponent = loading ? StyledLoadingButton : StyledButton;

  // Handle icon management for loading state
  const getStartIcon = () => {
    if (loading && ButtonComponent === StyledLoadingButton) {
      return undefined; // LoadingButton handles its own loading icon
    }
    return startIcon;
  };

  const getEndIcon = () => {
    if (loading && ButtonComponent === StyledLoadingButton) {
      return undefined; // LoadingButton handles its own loading icon
    }
    return endIcon;
  };

  return (
    <ButtonComponent
      variant={variant}
      size={size}
      disabled={disabled || loading}
      fullWidth={fullWidth}
      startIcon={getStartIcon()}
      endIcon={getEndIcon()}
      onClick={onClick}
      type={type}
      className={className}
      sx={sx}
      buttonType={buttonType}
      colorVariant={colorVariant}
      loading={loading}
      {...props}
    >
      {children}
    </ButtonComponent>
  );
};

EasyCheckButton.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['text', 'outlined', 'contained']),
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'error', 'info', 'warning', 'danger']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  buttonType: PropTypes.oneOf(['auth', 'action', 'form', 'icon', 'default']),
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  className: PropTypes.string,
  sx: PropTypes.object,
};

export default EasyCheckButton; 